import { getBasePath } from '@/utils/utils';
import { getServerGameInfo } from '@op/utils/game';
import { v4 as uuidv4 } from 'uuid';
import {
  ColumnsType,
  CommonColumnsType,
  DownloadType,
  DownloadParamsType,
  DownloadProps,
  DownloadConfigInfoType,
  CallbacksType,
} from '../types';
import * as downloadConfig from '../config';
import { downloadFile } from '@op/components/DownloadCommon/download-page/utils';
import { textToFile } from './textFile';
import { DBO_T, DBO_TM } from '@op/utils/format';
import {
  downloadParamsPrefix,
  safeSetItem,
} from '@op/utils/storageUtils';
import { objToUrl } from '@op/utils/url';

export function getFileName(
  name: string,
  filter: any,
  gameItem?: any,
  prefix = 'Opinions_',
  needFilterTime = true,
) {
  // 去掉name中的文件名非法字符
  const gameName = gameItem?.entity_name?.replace(/[\\/:*?."<>|]/g, '');
  if (needFilterTime) {
    return `${prefix}${gameName}_${name}_${DBO_TM.timeTitle({
      ...filter,
      link_text: '_',
    })}`;
  }

  return `${prefix}${gameName}_${name}`;
}

export async function getFileNameAndGameInfo(
  name: string,
  filter: any,
  gameInfo?: any,
  needFilterTime?: boolean,
) {
  const gameItem =
    gameInfo || (await getServerGameInfo(filter.edition_unified_id));
  // 这个名称有点问题，需要后期调整
  return {
    newFileName: getFileName(
      name,
      filter,
      gameItem,
      'Opinions_',
      needFilterTime,
    ),
    gameInfo: gameItem,
  };
}

export async function downloadAction({
  key,
  fileName,
  filter,
  auxiliaryParams,
  data,
  needFilterTime,
}: DownloadProps) {
  const { newFileName, gameInfo } = await getFileNameAndGameInfo(
    fileName,
    filter,
    undefined,
    needFilterTime,
  );

  const storageParams = {
    key,
    fileName: newFileName,
    filter,
    auxiliaryParams,
    gameInfo,
    data,
    downloadTime: new Date().getTime(),
  };

  const downloadId = uuidv4();

  // 写入失败时safeSetItem内部会逐级清理并提示，此时无需打开下载页
  const setResult = safeSetItem(
    localStorage,
    `${downloadParamsPrefix}-${downloadId}`,
    JSON.stringify(storageParams),
    downloadParamsPrefix,
  );

  if (!setResult) return;

  (window as any).aegis?.infoAll?.(
    JSON.stringify({
      action: 'download',
      downloadId,
      storageParams,
    }),
  );

  window.open(
    `${getBasePath()}/opinion/NewDownload?id=${downloadId}&gameid=${gameInfo?.edition_unified_id
    }`,
    '_blank',
  );
}

function aegisDownload(id?: string, params?: any) {
  (window as any).aegis?.infoAll?.(
    JSON.stringify({
      action: 'downloadPageInit',
      downloadId: id,
      storageParams: params,
    }),
  );
}

export function parseDownloadParams(map: any): DownloadProps | null {
  const id = map.get('id');
  let downLoadParams = localStorage.getItem('downLoadParams');
  try {
    // 获取数据阶段
    if (id) {
      downLoadParams = localStorage.getItem(`downLoadParams-${map.get('id')}`);
    }
    // 上报下载
    if (downLoadParams) {
      aegisDownload(id, downLoadParams);
      return JSON.parse(downLoadParams);
    }
  } catch (error) { }
  console.log(
    JSON.stringify({
      action: 'downloadPageInitFail',
      downloadId: id,
      storageParams: downLoadParams,
      storageParams2: localStorage.getItem('downLoadParams'),
      storageParams3: localStorage.getItem(`downLoadParams-${id}`),
    }),
  );
  return null;
}

function getDownloadFunc(
  { downloadFunc, customizeDownloadFunc }: DownloadConfigInfoType,
  { filter, ...otherInfo }: DownloadParamsType,
) {
  if (downloadFunc) {
    return () => downloadFunc?.(filter);
  }

  if (customizeDownloadFunc) {
    return () => customizeDownloadFunc?.({ filter, ...otherInfo });
  }

  return null;
}

/**
 * 直接生成文件
 * @param param0
 * @returns
 */
async function directDownloadFile({
  config,
  filter,
  callbacks,
  fileName,
  ...props
}: {
  config: DownloadConfigInfoType;
  callbacks: CallbacksType;
  fileName: string;
} & DownloadParamsType) {
  callbacks.begin({ type: 'file' });

  const func = getDownloadFunc(config, { ...props, filter });
  const res = await func?.();

  if (res instanceof ArrayBuffer) {
    downloadFile(res, fileName, config.mimeType);
  } else {
    callbacks.error({ type: 'file', tips: 'file_failed' });
  }
  callbacks.end({ type: 'file' });

  return null;
}

/**
 * 下载数据并转为文件
 * @param param0
 * @returns
 */
async function downloadDataToFile({
  config,
  filter,
  data,
  callbacks,
  ...props
}: {
  config: DownloadConfigInfoType;
  callbacks: CallbacksType;
} & DownloadParamsType) {
  const { dealWithDataFunc = ({ data }: any) => data } = config;
  callbacks?.begin({ type: 'xlsx' });
  let newData = data;

  // todo 还有一个循环下载所有数据的操作，后续可以写一个公共循环请求的方法，在downloadFunc内部调用
  if (!newData) {
    const func = getDownloadFunc(config, { ...props, filter });
    newData = await func?.();
  }

  callbacks?.end({ type: 'xlsx' });
  return dealWithDataFunc({ ...props, data: newData, filter });
}

/**
 * 下载数据转为文本文件
 * @param param0
 * @returns
 */
async function downloadText({
  config,
  filter,
  callbacks,
  fileName,
  fileType = 'txt',
  dealwithFile = text => text,
  ...props
}: {
  config: DownloadConfigInfoType;
  callbacks: CallbacksType;
  fileName: string;
  fileType?: 'txt' | 'html';
  dealwithFile?: (text: string) => string;
} & DownloadParamsType) {
  callbacks?.begin({ type: fileType });

  const func = getDownloadFunc(config, { ...props, filter });
  const data = await func?.();

  if (data.status === 'failed') {
    callbacks.error({ ...data, type: fileType });
  } else {
    callbacks?.end({ type: fileType });
    const fileText = dealwithFile(data.text);
    textToFile(fileText, fileName);
  }

  return null;
}

/**
 * 下载数据转为文本文件
 * @param param0
 * @returns
 */
async function downloadHtml(
  props: {
    config: DownloadConfigInfoType;
    callbacks: CallbacksType;
    fileName: string;
  } & DownloadParamsType,
) {
  // const html = DBO_T.mdToHtmlFile(text);
  return downloadText({
    ...props,
    fileName: `${props.fileName}.html`,
    fileType: 'html',
    dealwithFile: (text: string) => DBO_T.mdToHtmlFile(text),
  });
}

/**
 * 下载数据转为文本文件
 * @param param0
 * @returns
 */
async function downloadPdf({
  callbacks,
  config,
  filter,
  fileName,
  ...props
}: {
  config: DownloadConfigInfoType;
  callbacks: CallbacksType;
  fileName: string;
} & DownloadParamsType) {
  callbacks?.begin({ type: 'pdf' });

  const func = getDownloadFunc(config, { ...props, filter });
  const data = await func?.();

  if (data.status === 'failed') {
    callbacks.error({ ...data, type: 'pdf' });
  } else {
    try {
      callbacks?.end({ type: 'pdf' });

      const { html } = DBO_T.mdToHtml(data.text);
      const tempDiv = document.createElement('div');
      tempDiv.classList.add('dbo-md-download-wrapper');

      tempDiv.innerHTML = html;

      DBO_T.htmlToPdf({
        ref: { current: tempDiv },
        fileName,
      });
    } catch (error) {
      callbacks?.error({ ...data, type: 'pdf', tips: 'common' });
    }
  }

  return null;
}

/**
 * COS 异步下载
 * 1. 调用 prepareDownloadFunc 通知后端准备数据
 * 2. 通过 prepareResponseExtractor 提取任务标识符
 * 3. 通过 pollRequestBuilder 构建轮询参数，循环调用 pollDownloadFunc
 * 4. 通过 pollResponseHandler 检测状态，拿到 URL 后打开并关闭 tab
 */
async function downloadCos(propsConfig: {
  config: DownloadConfigInfoType;
  callbacks: CallbacksType;
  fileName: string;
} & DownloadParamsType) {
  const {
    config,
    filter,
    callbacks,
    ...props
  } = propsConfig;
  const {
    prepareDownloadFunc,
    pollDownloadFunc,
    pollConfig = {},
  } = config;
  const {
    interval = 3000,
    maxRetries = 60,
    prepareResponseExtractor,
    pollRequestBuilder,
    pollResponseHandler,
  } = pollConfig;

  // 默认提取器: res => res?.job_id（http() 已自动解构 data 层）
  const extractId = prepareResponseExtractor || ((res: any) => res?.job_id);
  // 默认构建器: (id, filter) => ({ jobId: id, filter })
  const buildParams = pollRequestBuilder || ((id: string, f: any) => ({ jobId: id, filter: f }));
  // 默认响应处理器（http() 已自动解构 data 层）
  const handleResponse = pollResponseHandler || ((res: any) => {
    if (res?.status === 'success' && res?.url) {
      return { status: 'success' as const, url: res.url };
    }
    if (res?.status === 'failed') {
      return { status: 'failed' as const };
    }
    return { status: 'pending' as const };
  });

  callbacks?.begin({ type: 'cos' });

  try {
    // 第一步：通知后端准备数据，提取标识符
    const prepareFunc = prepareDownloadFunc || (() => ({}));
    const prepareRes = await prepareFunc(filter, propsConfig);
    const identifier = extractId(prepareRes);

    if (!identifier) {
      console.error('downloadCos: failed to extract identifier from prepare response', prepareRes);
      callbacks?.error({ type: 'cos', tips: 'cos_failed' });
      return null;
    }

    // 第二步：轮询获取下载链接
    let retries = 0;
    let url = '';
    const pollParams = buildParams(identifier, filter);

    while (retries < maxRetries) {
      retries += 1;
      // eslint-disable-next-line no-await-in-loop
      const pollRes = await pollDownloadFunc?.(pollParams);
      const result = handleResponse(pollRes);

      if (result.status === 'success') {
        url = result.url;
        break;
      }

      if (result.status === 'failed') {
        console.error('downloadCos: poll returned failed', { identifier, retries });
        break;
      }

      // pending，等待后继续
      if (retries < maxRetries) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise(resolve => setTimeout(resolve, interval));
      }
    }

    if (!url) {
      callbacks?.error({ type: 'cos', tips: 'cos_failed' });
      return null;
    }

    // 第三步：通过隐藏链接触发下载，无需新开窗口
    callbacks?.end({ type: 'cos' });
    const link = document.createElement('a');
    link.href = url;
    link.style.display = 'none';
    // 用 props 中的 fileName 作为默认文件名（跨域时浏览器可能忽略此属性）
    if (props.fileName) {
      link.download = props.fileName;
    }
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return null;
  } catch (e) {
    console.log('error downloadCos', e);
    callbacks?.error({ type: 'cos', tips: 'cos_failed' });
    return null;
  }
}

const downloadTypeMap: {
  [key in DownloadType]: (
    item: {
      fileName: string;
      config: DownloadConfigInfoType;
      callbacks: CallbacksType;
    } & DownloadParamsType,
  ) => Promise<Array<any> | null>;
} = {
  txt: downloadText,
  file: directDownloadFile,
  xlsx: downloadDataToFile,
  html: downloadHtml,
  pdf: downloadPdf,
  zip: downloadDataToFile,
  page: downloadDataToFile,
  fileZip: downloadDataToFile,
  cos: downloadCos,
};

/**
 * 处理完成所有数据
 * @param data  DownloadProps
 * @returns
 */
export async function getDownloadData({
  callbacks,
  ...downloadParams
}: DownloadProps & { callbacks: CallbacksType }): Promise<any | null> {
  const { key } = downloadParams;
  const config = (downloadConfig as any)[key];
  const type: DownloadType = config.type || 'xlsx';
  return downloadTypeMap[type]({ ...downloadParams, config, callbacks });
}

 

/**
 * 分页拉取全部数据
 * 适用于需要逐页请求并合并所有结果的下载场景
 * @param apiFunc   API 请求函数，接收 { data: filter } 参数
 * @param filter    请求参数（不含 page/page_size）
 * @param pageSize  每页条数，默认 1000
 * @returns         合并后的全部列表数据
 */
export async function fetchAllPages<T = any>(
  apiFunc: (config: { data: any }) => Promise<any>,
  filter: any,
  pageSize = 1000,
): Promise<T[]> {
  let page = 0;
  let totalPage = 0;
  const allData: T[] = [];

  do {
    page += 1;
    const res: any = await apiFunc({ data: { ...filter, page, page_size: pageSize } });
    const list: T[] = res?.data?.list || res?.list || [];
    allData.push(...list);

    if (totalPage === 0) {
      const total = res?.data?.total || res?.total || 0;
      totalPage = Math.ceil(total / pageSize);
    }
  } while (page < totalPage);

  return allData;
}

/**
 * 获取下载列配置
 * @param data  DownloadProps
 * @returns
 */
export function getDownloadConfig(
  props: DownloadParamsType,
): DownloadConfigInfoType {
  const { key } = props;
  const config = (downloadConfig as any)[key];
  if (!config) {
    console.warn(
      `[download] getDownloadConfig 未找到配置 key=${JSON.stringify(key)}，可用配置(${Object.keys(downloadConfig).length}):`,
      Object.keys(downloadConfig),
    );
  }
  return { ...config };
}

export function getDatabrainUrlRender(gameid: string) {
  return (item: any) => {
    const data = {
      gameid,
      comment_time: item.comment_time,
    };
    const paramsUrl = objToUrl(data);
    return `${location.origin}/opinion/Feeds/Feeds/Details/${item.comment_uin}?${paramsUrl}`;
  };
}
